import { PhuturePoolAbi } from 'abis/PhuturePoolAbi'
import { WAD } from 'app-constants'
import { encodeFunctionData, isAddressEqual, zeroAddress, type Address } from 'viem'
import { base } from 'viem/chains'
import { Config } from 'wagmi'
import { ZeroExAggregator } from './aggregators/0x-aggregators'
import { PendleAggregator } from './pendle'
import { Trade } from './redeem/interfaces'
import { DenominationWrapperAbi } from 'abis/DenominationWrapperAbi'

type Asset =
  | {
      type: 'ERC20'
    }
  | {
      type: 'PENDLE_PT'
      market: Address
      underlying: Address
    }
  | {
      type: 'PENDLE_LP'
    }
  | {
      type: 'PHUTURE_WRAPPED_PENDLE_LP'
      pool: Address
      asset: Address
    }
  | {
      type: 'PHUTURE_DENOMINATION_WRAPPED_OLD'
      factor: bigint
      asset: Address
    }
  | {
      type: 'PHUTURE_DENOMINATION_WRAPPED'
      factor: bigint
      asset: Address
    }

export const Assets: Record<string, Asset> = {
  ['42161:0xD5dd6244c65688bBdfF156bf7650cED3FB05823A'.toLowerCase()]: {
    type: 'PHUTURE_WRAPPED_PENDLE_LP',
    pool: '0x3CF870E569c77A7D789d3dF7DBf772162185529A',
    asset: '0xf9F9779d8fF604732EBA9AD345E6A27EF5c2a9d6',
  },
  ['42161:0x01928bf4378e0303a133904391E2D72121aE5028'.toLowerCase()]: {
    type: 'PHUTURE_WRAPPED_PENDLE_LP',
    pool: '0x3CF870E569c77A7D789d3dF7DBf772162185529A',
    asset: '0xED99fC8bdB8E9e7B8240f62f69609a125A0Fbf14',
  },
  ['42161:0x1746b55cb752DF539A10F71772ca725fEC3fe63d'.toLowerCase()]: {
    type: 'PHUTURE_WRAPPED_PENDLE_LP',
    pool: '0x3CF870E569c77A7D789d3dF7DBf772162185529A',
    asset: '0x35f3dB08a6e9cB4391348b0B404F493E7ae264c0',
  },
  ['42161:0xb8b0a120F6A68Dd06209619F62429fB1a8e92feC'.toLowerCase()]: {
    type: 'PENDLE_PT',
    market: '0xf9F9779d8fF604732EBA9AD345E6A27EF5c2a9d6',
    underlying: '0x35751007a407ca6FEFfE80b3cB397736D2cf4dbe',
  },
  ['42161:0x30c98c0139B62290E26aC2a2158AC341Dcaf1333'.toLowerCase()]: {
    type: 'PENDLE_PT',
    market: '0xED99fC8bdB8E9e7B8240f62f69609a125A0Fbf14',
    underlying: '0x4186BFC76E2E237523CBC30FD220FE055156b41F',
  },
  ['42161:0x2CCFce9bE49465CC6f947b5F6aC9383673733Da9'.toLowerCase()]: {
    type: 'PENDLE_PT',
    market: '0x35f3dB08a6e9cB4391348b0B404F493E7ae264c0',
    underlying: '0x2416092f143378750bb29b79eD961ab195CcEea5',
  },
  [`${base.id}:0x61a287fc37559c71ba7cf6c35f3a5716dc91d265`.toLowerCase()]: {
    type: 'PHUTURE_DENOMINATION_WRAPPED_OLD',
    factor: BigInt('1000000000000000000000000'), // 1000000e18
    asset: '0x2Da56AcB9Ea78330f947bD57C54119Debda7AF71',
  },
  [`${base.id}:0x66af6b21aad17849bf8db8844b26d5cdd1b7ac86`.toLowerCase()]: {
    type: 'PHUTURE_DENOMINATION_WRAPPED',
    factor: BigInt('1000000000000000000000000'), // 1000000e18
    asset: '0x2Da56AcB9Ea78330f947bD57C54119Debda7AF71',
  },
  [`${base.id}:0xeff13712b5efacf5213f9a76bc58d89f037d71d3`.toLowerCase()]: {
    type: 'PHUTURE_DENOMINATION_WRAPPED',
    factor: BigInt('1000000000000000000000000'), // 1000000e18
    asset: '0x2Da56AcB9Ea78330f947bD57C54119Debda7AF71',
  },
}

const zeroExAggregator = ZeroExAggregator.builder.build()
const pendleAggregator = PendleAggregator.builder
  .withBaseUrl('https://api-v2.pendle.finance/sdk/api/v1')
  .build()

export const swap = async (
  client: Config,
  sellCurrency: Address,
  sellAmount: bigint,
  buyCurrency: Address,
  escrow: Address,
  recipient: Address,
  scale?: bigint,
): Promise<Trade[]> => {
  const trades: Trade[] = []
  const chainId = client.getClient().chain.id

  const assetInfo = Assets[`${chainId}:${sellCurrency.toLowerCase()}`] ?? {
    type: 'ERC20',
  }
  const isZeta = false
  switch (assetInfo.type) {
    case 'ERC20': {
      const quote = await zeroExAggregator.quote(
        chainId,
        sellCurrency,
        buyCurrency,
        (sellAmount * (scale ?? (isZeta ? 100n : 101n))) / 100n,
        escrow,
      )

      trades.push({
        skipRevert: false,
        currency: sellCurrency,
        allowanceTarget: quote.allowanceTarget,
        addr: quote.to,
        value: isAddressEqual(sellCurrency, zeroAddress) ? 0n : quote.value,
        data: quote.data,
      })
      break
    }
    case 'PENDLE_PT': {
      const quote = await pendleAggregator.swapExactPtForToken(
        chainId,
        recipient,
        assetInfo.market,
        buyCurrency,
        (sellAmount * (scale ?? 99n)) / 100n,
        0.01,
      )

      trades.push({
        skipRevert: true,
        currency: sellCurrency,
        allowanceTarget: quote.to,
        addr: quote.to,
        value: 0n,
        data: quote.data,
      })
      break
    }
    case 'PENDLE_LP': {
      const quote = await pendleAggregator.removeLiquiditySingleToken(
        chainId,
        recipient,
        sellCurrency,
        (sellAmount * (scale ?? 99n)) / 100n,
        buyCurrency,
        0.01,
      )

      trades.push({
        skipRevert: false,
        currency: sellCurrency,
        allowanceTarget: quote.to,
        addr: quote.to,
        value: 0n,
        data: quote.data,
      })
      break
    }
    case 'PHUTURE_WRAPPED_PENDLE_LP': {
      const poolRedeem = encodeFunctionData({
        abi: PhuturePoolAbi,
        functionName: 'redeem',
        args: [assetInfo.asset, (sellAmount * (scale ?? 99n)) / 100n, escrow, escrow],
      })

      trades.push({
        skipRevert: false,
        currency: sellCurrency,
        allowanceTarget: assetInfo.pool,
        addr: assetInfo.pool,
        value: 0n,
        data: poolRedeem,
      })

      const quote = await pendleAggregator.removeLiquiditySingleToken(
        chainId,
        recipient,
        assetInfo.asset,
        (sellAmount * (scale ?? 99n)) / 100n,
        buyCurrency,
        0.01,
      )

      trades.push({
        skipRevert: false,
        currency: assetInfo.asset,
        allowanceTarget: quote.to,
        addr: quote.to,
        value: 0n,
        data: quote.data,
      })
      break
    }
    case 'PHUTURE_DENOMINATION_WRAPPED': {
      const scaledAmount = (sellAmount * (scale ?? 101n)) / 100n

      const data = encodeFunctionData({
        abi: [
          {
            type: 'function',
            name: 'withdraw',
            outputs: [{ name: 'originalAmount', type: 'uint256', internalType: 'uint256' }],
            stateMutability: 'nonpayable',
          },
        ],
        functionName: 'withdraw',
      })

      trades.push({
        skipRevert: false,
        currency: sellCurrency,
        allowanceTarget: zeroAddress,
        addr: sellCurrency,
        value: 0n,
        data: data,
      })

      const quote = await zeroExAggregator.quote(
        chainId,
        assetInfo.asset,
        buyCurrency,
        (scaledAmount * assetInfo.factor) / WAD,
      )

      trades.push({
        skipRevert: false,
        currency: assetInfo.asset,
        allowanceTarget: quote.allowanceTarget,
        addr: quote.to,
        value: 0n,
        data: quote.data,
      })
      break
    }
    // TODO: delete
    case 'PHUTURE_DENOMINATION_WRAPPED_OLD': {
      const scaledAmount = (sellAmount * (scale ?? 99n)) / 100n

      const data = encodeFunctionData({
        abi: DenominationWrapperAbi,
        functionName: 'withdraw',
        args: [scaledAmount],
      })

      trades.push({
        skipRevert: false,
        currency: sellCurrency,
        allowanceTarget: zeroAddress,
        addr: sellCurrency,
        value: 0n,
        data: data,
      })

      const quote = await zeroExAggregator.quote(
        chainId,
        assetInfo.asset,
        buyCurrency,
        (scaledAmount * assetInfo.factor) / WAD,
      )

      trades.push({
        skipRevert: false,
        currency: assetInfo.asset,
        allowanceTarget: quote.allowanceTarget,
        addr: quote.to,
        value: 0n,
        data: quote.data,
      })
      break
    }
    default:
      console.error('UNKNOWN ASSET TYPE FOR', sellCurrency)
      break
  }

  return trades
}
